<template>
  <div
    :class="{ [$style.banner]: true, [$style.successMessage]: variant === 'success' }"
    :data-testid="
      variant === 'success'
        ? 'tenant-plus-highlight-success-message'
        : 'tenant-plus-highlight-message'
    "
  >
    <div :class="$style.textContent">
      <span v-html="text1"></span>
      <div :class="$style.noBreak">
        <span v-html="t('contactForm.tenantPlus.tenantWord')"></span>
        <TenantPlusBadge :class="$style.badge" small />
      </div>
      <span>{{ text2 }}</span>
    </div>
    <img v-if="variant !== 'success'" :class="$style.image" :src="imageSrc" loading="lazy" />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

import tenantPlusIconHg from '@/assets/TenantPlus/tenant-plus-contact-form-plusIcon-hg.svg?url';
import tenantPlusIconIs24 from '@/assets/TenantPlus/tenant-plus-contact-form-plusIcon-is24.webp?url';
import TenantPlusBadge from '@/components/TenantPlus/Badge/TenantPlusBadge.vue';
import { isPlatformHg } from '@/helpers/platformDetection';
import { useI18n } from 'vue-i18n';

export type TenantPlusVariant = 'banner' | 'success';

defineOptions({
  name: 'TenantPlusHighlightMessage',
});

interface Props {
  variant?: TenantPlusVariant;
}
const props = withDefaults(defineProps<Props>(), {
  variant: 'banner',
});

const { t } = useI18n();

const imageSrc = isPlatformHg() ? tenantPlusIconHg : tenantPlusIconIs24;

const text1 = computed((): string =>
  props.variant == 'banner'
    ? t('contactForm.tenantPlus.text1')
    : t('contactForm.submitFeedback.successTenantPlus.message1'),
);

const text2 = computed((): string =>
  props.variant == 'banner'
    ? t('contactForm.tenantPlus.text2')
    : t('contactForm.submitFeedback.successTenantPlus.message2'),
);
</script>

<style lang="scss" module>
@import '@/assets/variables';
$banner-image-height: 72px;

.banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: $border-radius-l;
  border: 1px solid $color-grey-60;
  background-color: $color-white;
}

.textContent {
  @include body-2;
  padding: $space-75;
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;

  > * {
    display: inline;
    vertical-align: middle;
  }
}

.badge {
  transform: translateY(-1px);
  display: inline-flex;
  vertical-align: middle;
  margin: 0 $space-25;
}

.image {
  display: block;
  height: $banner-image-height;
  width: auto;
  flex-shrink: 0;
  object-fit: contain;
}

.successMessage {
  margin-top: $space-75;
}

.noBreak {
  white-space: nowrap;
}
</style>
